import * as pc from "playcanvas";
import type { GameClient } from "../net/GameClient";
import type { AABB } from "../scene/Arena";

/** Tight, dense droplet sprite for the airborne spray particles. */
function createBloodTexture(device: pc.GraphicsDevice): pc.Texture {
  const size = 32;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    // Saturated core with a crisp falloff — reads as a wet droplet, not a soft cloud.
    gradient.addColorStop(0, "rgba(150, 12, 12, 1)");
    gradient.addColorStop(0.35, "rgba(110, 6, 6, 0.95)");
    gradient.addColorStop(0.7, "rgba(70, 2, 2, 0.5)");
    gradient.addColorStop(1, "rgba(40, 0, 0, 0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  }

  const texture = new pc.Texture(device, {
    width: size,
    height: size,
    format: pc.PIXELFORMAT_R8_G8_B8_A8,
    mipmaps: false,
  });
  texture.setSource(canvas);
  return texture;
}

/**
 * Procedural blood-splatter sprite for the surface decals: an irregular
 * central blob plus scattered droplets and a few radiating streaks. Each
 * call randomises the layout, so a small set gives plenty of variety.
 */
function createBloodSplatTexture(device: pc.GraphicsDevice): pc.Texture {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    const cx = size / 2;
    const cy = size / 2;
    const red = (a: number): string =>
      `rgba(${95 + Math.floor(Math.random() * 35)}, ${4 + Math.floor(Math.random() * 10)}, ${4 + Math.floor(Math.random() * 8)}, ${a})`;

    // Central blob — a few overlapping circles for an organic edge.
    for (let i = 0; i < 5; i++) {
      const r = size * (0.15 + Math.random() * 0.12);
      const ox = cx + (Math.random() - 0.5) * size * 0.12;
      const oy = cy + (Math.random() - 0.5) * size * 0.12;
      ctx.fillStyle = red(0.85);
      ctx.beginPath();
      ctx.arc(ox, oy, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // Scattered droplets + occasional streaks radiating outward.
    const drops = 14 + Math.floor(Math.random() * 10);
    for (let i = 0; i < drops; i++) {
      const ang = Math.random() * Math.PI * 2;
      const dist = size * (0.22 + Math.random() * 0.26);
      const dx = cx + Math.cos(ang) * dist;
      const dy = cy + Math.sin(ang) * dist;
      const r = size * (0.012 + Math.random() * 0.04);
      ctx.fillStyle = red(0.5 + Math.random() * 0.4);
      ctx.beginPath();
      ctx.arc(dx, dy, r, 0, Math.PI * 2);
      ctx.fill();
      if (Math.random() < 0.4) {
        ctx.strokeStyle = red(0.4);
        ctx.lineWidth = r * (0.5 + Math.random());
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(ang) * size * 0.16, cy + Math.sin(ang) * size * 0.16);
        ctx.lineTo(dx, dy);
        ctx.stroke();
      }
    }
  }

  const texture = new pc.Texture(device, {
    width: size,
    height: size,
    format: pc.PIXELFORMAT_R8_G8_B8_A8,
    mipmaps: true,
  });
  texture.setSource(canvas);
  return texture;
}

/** One reusable blood instance: an orientable particle emitter + wound flash. */
interface BloodSlot {
  root: pc.Entity;
  ps: pc.ParticleSystemComponent;
  flash: pc.Entity;
  flashMat: pc.StandardMaterial;
  age: number;
  active: boolean;
}

/** One reusable surface stain (floor pool or wall spray). */
interface DecalSlot {
  entity: pc.Entity;
  mat: pc.StandardMaterial;
  age: number;
  life: number;
  active: boolean;
}

/**
 * BloodSplatter — fine blood spray spawned at the body-impact point on every
 * authoritative `hit` event, plus a brief wound flash and lingering stains
 * that stick to the world (a pool on the ground under the victim and, when a
 * wall sits just behind them, a spray on it).
 *
 * Everything is pooled and built up front, so a hit only repositions idle
 * slots and replays them — no per-hit `Entity` / `particlesystem` / material
 * allocation. The directional spray lives in local space
 * (`localVelocityGraph`, steered by the slot's rotation) while gravity stays
 * in world space so droplets always fall down. A single `update` loop advances
 * every active particle + decal slot. See `legacy/effects/BloodSplatter.ts`
 * for the previous (oversized, allocate-per-hit) version.
 */
export class BloodSplatter {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly collisionBoxes: AABB[];
  private readonly unsubs: Array<() => void> = [];
  private bloodTexture: pc.Texture | null = null;
  private readonly splatTextures: pc.Texture[] = [];
  private readonly slots: BloodSlot[] = [];
  private readonly decals: DecalSlot[] = [];
  private nextSlot = 0;
  private nextDecal = 0;
  private readonly onUpdate: (dt: number) => void;

  private static readonly POOL_SIZE = 8;
  private static readonly DECAL_POOL_SIZE = 24;
  private static readonly SPLAT_VARIANTS = 3;
  private static readonly PARTICLE_LIFETIME = 0.45;
  private static readonly FLASH_LIFETIME = 0.28;
  private static readonly GRAVITY = 14;
  private static readonly NUM_PARTICLES = 14;
  private static readonly FLASH_BASE = 0.03;
  private static readonly FLASH_GROW = 0.03;
  private static readonly FLASH_INTENSITY = 3.0;
  private static readonly DECAL_LIFE = 9.0;
  private static readonly WALL_RAY_RANGE = 3.5;
  private static readonly FLOOR_Y = 0.02;

  constructor(app: pc.AppBase, client: GameClient, collisionBoxes: AABB[] = []) {
    this.app = app;
    this.client = client;
    this.collisionBoxes = collisionBoxes;

    if (this.app.graphicsDevice) {
      this.bloodTexture = createBloodTexture(this.app.graphicsDevice);
      for (let i = 0; i < BloodSplatter.SPLAT_VARIANTS; i++) {
        this.splatTextures.push(createBloodSplatTexture(this.app.graphicsDevice));
      }
    }

    // Build the reusable pools up front — no per-hit allocation afterwards.
    for (let i = 0; i < BloodSplatter.POOL_SIZE; i++) {
      this.slots.push(this.buildSlot());
    }
    for (let i = 0; i < BloodSplatter.DECAL_POOL_SIZE; i++) {
      this.decals.push(this.buildDecal(i));
    }

    // One persistent update loop drives all active slots.
    this.onUpdate = (dt: number) => this.tick(dt);
    this.app.on("update", this.onUpdate);

    this.unsubs.push(
      this.client.onHit((msg) => {
        if (msg.hitX === undefined || msg.hitY === undefined || msg.hitZ === undefined) return;
        let nx = 0, ny = 0, nz = 1;
        if (msg.attackerX !== undefined && msg.attackerY !== undefined && msg.attackerZ !== undefined) {
          const dx = msg.hitX - msg.attackerX;
          const dy = msg.hitY - msg.attackerY;
          const dz = msg.hitZ - msg.attackerZ;
          const m = Math.hypot(dx, dy, dz);
          if (m > 1e-4) { nx = dx / m; ny = dy / m; nz = dz / m; }
        }
        this.acquire(msg.hitX, msg.hitY, msg.hitZ, nx, ny, nz);
      }),
    );
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    this.app.off("update", this.onUpdate);
    for (const s of this.slots) {
      s.root.destroy(); // destroys the child flash entity too
      s.flashMat.destroy();
    }
    this.slots.length = 0;
    for (const d of this.decals) {
      d.entity.destroy();
      d.mat.destroy();
    }
    this.decals.length = 0;
    if (this.bloodTexture) {
      this.bloodTexture.destroy();
      this.bloodTexture = null;
    }
    for (const t of this.splatTextures) t.destroy();
    this.splatTextures.length = 0;
  }

  /** Build a single idle spray instance (emitter + flash child). */
  private buildSlot(): BloodSlot {
    const forwardMag = 1.6;
    const spread = 1.1;
    const drop = BloodSplatter.GRAVITY * BloodSplatter.PARTICLE_LIFETIME;

    // Directional spray in LOCAL space along -Z (PlayCanvas `lookAt` forward),
    // with lateral jitter. Steered each hit by the slot's rotation.
    const localVel = new pc.CurveSet([
      [0, -spread, 1, -spread],
      [0, -spread, 1, -spread],
      [0, -forwardMag - spread, 1, -forwardMag - spread],
    ]);
    const localVel2 = new pc.CurveSet([
      [0, spread, 1, spread],
      [0, spread, 1, spread],
      [0, -forwardMag + spread, 1, -forwardMag + spread],
    ]);
    // Gravity in WORLD space so droplets fall down regardless of facing.
    const worldVel = new pc.CurveSet([
      [0, 0, 1, 0],
      [0, 0, 1, -drop],
      [0, 0, 1, 0],
    ]);
    const worldVel2 = new pc.CurveSet([
      [0, 0, 1, 0],
      [0, 0, 1, -drop],
      [0, 0, 1, 0],
    ]);

    // Fine droplets: ~1 cm growing to ~2.5–4 cm (was 5–25 cm in the legacy version).
    const scaleGraph = new pc.Curve([0, 0.01, 1, 0.025]);
    const scaleGraph2 = new pc.Curve([0, 0.018, 1, 0.04]);
    const alphaGraph = new pc.Curve([0, 1, 0.8, 1, 1, 0]);

    const root = new pc.Entity("blood-particles");
    root.addComponent("particlesystem", {
      numParticles: BloodSplatter.NUM_PARTICLES,
      lifetime: BloodSplatter.PARTICLE_LIFETIME,
      rate: 0, // Burst emission
      colorMap: this.bloodTexture,
      emitterShape: pc.EMITTERSHAPE_SPHERE,
      emitterRadius: 0.03,
      localVelocityGraph: localVel,
      localVelocityGraph2: localVel2,
      velocityGraph: worldVel,
      velocityGraph2: worldVel2,
      scaleGraph: scaleGraph,
      scaleGraph2: scaleGraph2,
      alphaGraph: alphaGraph,
      blendType: pc.BLEND_NORMAL,
      loop: false,
      autoPlay: false,
      depthWrite: false,
      sort: pc.SORTMODE_BACK2FRONT,
    });
    root.enabled = false;
    this.app.root.addChild(root);

    // Flash sphere — the "wound" glow. Child of root so it tracks the impact.
    const flash = new pc.Entity("blood-flash");
    flash.addComponent("render", { type: "sphere" });
    flash.setLocalScale(BloodSplatter.FLASH_BASE, BloodSplatter.FLASH_BASE, BloodSplatter.FLASH_BASE);
    const flashMat = new pc.StandardMaterial();
    flashMat.diffuse = new pc.Color(0, 0, 0);
    flashMat.emissive = new pc.Color(0.95, 0.08, 0.05);
    flashMat.emissiveIntensity = BloodSplatter.FLASH_INTENSITY;
    flashMat.useLighting = false;
    flashMat.blendType = pc.BLEND_ADDITIVE;
    flashMat.depthWrite = false;
    flashMat.update();
    if (flash.render) {
      flash.render.material = flashMat;
      flash.render.castShadows = false;
      flash.render.receiveShadows = false;
    }
    root.addChild(flash);

    return { root, ps: root.particlesystem!, flash, flashMat, age: 0, active: false };
  }

  /** Build a single idle decal plane (textured blood stain). */
  private buildDecal(index: number): DecalSlot {
    const entity = new pc.Entity("blood-decal");
    entity.addComponent("render", { type: "plane" });

    const mat = new pc.StandardMaterial();
    mat.diffuse = new pc.Color(1, 1, 1); // texture carries the dark-red colour
    const tex = this.splatTextures.length
      ? this.splatTextures[index % this.splatTextures.length]
      : null;
    if (tex) {
      mat.diffuseMap = tex;
      mat.opacityMap = tex;
      mat.opacityMapChannel = "a";
    }
    mat.useLighting = true;
    mat.blendType = pc.BLEND_NORMAL;
    mat.depthWrite = false;
    mat.cull = pc.CULLFACE_NONE; // double-sided — visible regardless of plane facing
    mat.update();
    if (entity.render) {
      entity.render.material = mat;
      entity.render.castShadows = false;
      entity.render.receiveShadows = false;
    }
    entity.enabled = false;
    this.app.root.addChild(entity);

    return { entity, mat, age: 0, life: BloodSplatter.DECAL_LIFE, active: false };
  }

  /** Reuse the next spray slot + place lingering stains — no allocation. */
  private acquire(x: number, y: number, z: number, nx: number, ny: number, nz: number): void {
    const slot = this.slots[this.nextSlot];
    if (!slot) return;
    this.nextSlot = (this.nextSlot + 1) % this.slots.length;

    slot.root.enabled = true;
    slot.root.setPosition(x, y, z);
    // Orient local -Z toward the impact normal so the spray flies off the wound.
    // Swap the up vector for near-vertical shots to avoid a degenerate lookAt.
    if (Math.abs(ny) > 0.95) {
      slot.root.lookAt(x + nx, y + ny, z + nz, 1, 0, 0);
    } else {
      slot.root.lookAt(x + nx, y + ny, z + nz, 0, 1, 0);
    }

    // Replay the burst.
    slot.ps.reset();
    slot.ps.play();

    // Reset the wound flash.
    slot.flashMat.emissiveIntensity = BloodSplatter.FLASH_INTENSITY;
    slot.flashMat.update();
    slot.flash.setLocalScale(BloodSplatter.FLASH_BASE, BloodSplatter.FLASH_BASE, BloodSplatter.FLASH_BASE);
    slot.flash.enabled = true;

    slot.age = 0;
    slot.active = true;

    // Lingering stains. A pool on the ground below the victim always reads;
    // a spray on the wall just behind them adds depth when one is close.
    this.placeFloorPool(x, z);
    this.placeWallSpray(x, y, z, nx, ny, nz);
  }

  /** Horizontal blood pool on the ground directly under the impact. */
  private placeFloorPool(x: number, z: number): void {
    const d = this.nextDecalSlot();
    if (!d) return;
    d.entity.enabled = true;
    d.entity.setPosition(x, BloodSplatter.FLOOR_Y, z);
    // Plane faces +Y natively — just spin it for variety.
    d.entity.setEulerAngles(0, Math.random() * 360, 0);
    const s = 0.4 + Math.random() * 0.4; // 0.4–0.8 m pool
    d.entity.setLocalScale(s, 1, s);
    this.armDecal(d);
  }

  /** Spray decal on the wall just behind the victim, along the bullet path. */
  private placeWallSpray(x: number, y: number, z: number, nx: number, ny: number, nz: number): void {
    if (!this.collisionBoxes.length) return;
    if (Math.abs(nx) + Math.abs(ny) + Math.abs(nz) < 1e-3) return;
    const hit = this.rayHit(x, y, z, nx, ny, nz, BloodSplatter.WALL_RAY_RANGE);
    if (!hit) return;

    const d = this.nextDecalSlot();
    if (!d) return;
    const px = x + nx * hit.t;
    const py = y + ny * hit.t;
    const pz = z + nz * hit.t;
    d.entity.enabled = true;
    // Lift off the surface a touch to dodge z-fighting, then orient the plane
    // to face out along the wall normal (same trick as the bullet-hole decal).
    d.entity.setPosition(px + hit.nx * 0.012, py + hit.ny * 0.012, pz + hit.nz * 0.012);
    d.entity.lookAt(px + hit.nx, py + hit.ny, pz + hit.nz);
    d.entity.rotateLocal(90, 0, 0);
    d.entity.rotateLocal(0, Math.random() * 360, 0);
    const s = 0.3 + Math.random() * 0.3; // 0.3–0.6 m spray
    d.entity.setLocalScale(s, 1, s);
    this.armDecal(d);
  }

  private nextDecalSlot(): DecalSlot | null {
    const d = this.decals[this.nextDecal];
    if (!d) return null;
    this.nextDecal = (this.nextDecal + 1) % this.decals.length;
    return d;
  }

  private armDecal(d: DecalSlot): void {
    d.mat.opacity = 1;
    d.mat.update();
    d.age = 0;
    d.life = BloodSplatter.DECAL_LIFE;
    d.active = true;
  }

  /**
   * Ray vs all collision AABBs (slab method). Returns the nearest positive hit
   * within `maxRange` with its surface normal, or null. Mirrors `Tracer`'s
   * `rayWorldHit` but kept local to avoid coupling the two effects.
   */
  private rayHit(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    maxRange: number,
  ): { t: number; nx: number; ny: number; nz: number } | null {
    let bestT = maxRange;
    let found = false;
    let bnAxis = 1, bnSign = 1;
    for (const b of this.collisionBoxes) {
      let tNear = -Infinity;
      let tFar = Infinity;
      let nearAxis = 0;
      let nearSign = 1;
      const slab = (oC: number, dC: number, lo: number, hi: number, axis: number): boolean => {
        if (Math.abs(dC) < 1e-9) return !(oC < lo || oC > hi);
        let t0 = (lo - oC) / dC;
        let t1 = (hi - oC) / dC;
        let sign = -1;
        if (t0 > t1) { const tmp = t0; t0 = t1; t1 = tmp; sign = 1; }
        if (t0 > tNear) { tNear = t0; nearAxis = axis; nearSign = sign; }
        if (t1 < tFar) tFar = t1;
        return tNear <= tFar;
      };
      if (!slab(ox, dx, b.minX, b.maxX, 0)) continue;
      if (!slab(oy, dy, b.minY, b.maxY, 1)) continue;
      if (!slab(oz, dz, b.minZ, b.maxZ, 2)) continue;
      if (tNear < 0 || tNear > tFar || tNear >= bestT) continue;
      bestT = tNear;
      found = true;
      bnAxis = nearAxis;
      bnSign = nearSign;
    }
    if (!found) return null;
    return {
      t: bestT,
      nx: bnAxis === 0 ? bnSign : 0,
      ny: bnAxis === 1 ? bnSign : 0,
      nz: bnAxis === 2 ? bnSign : 0,
    };
  }

  /** Advance every active slot: flash decay, decal fade, recycle when done. */
  private tick(dt: number): void {
    for (const slot of this.slots) {
      if (!slot.active) continue;
      slot.age += dt;

      const fT = slot.age / BloodSplatter.FLASH_LIFETIME;
      if (fT < 1) {
        slot.flashMat.emissiveIntensity = BloodSplatter.FLASH_INTENSITY * Math.pow(1 - fT, 1.6);
        const fs = BloodSplatter.FLASH_BASE + BloodSplatter.FLASH_GROW * (1 - fT);
        slot.flash.setLocalScale(fs, fs, fs);
        slot.flashMat.update();
      } else if (slot.flash.enabled) {
        slot.flash.enabled = false;
      }

      if (slot.age >= BloodSplatter.PARTICLE_LIFETIME) {
        slot.active = false;
        slot.root.enabled = false; // disables the emitter + flash child
      }
    }

    for (const d of this.decals) {
      if (!d.active) continue;
      d.age += dt;
      const t = d.age / d.life;
      if (t >= 1) {
        d.active = false;
        d.entity.enabled = false;
        continue;
      }
      // Stay solid, then fade over the last quarter of life.
      if (t > 0.75) {
        d.mat.opacity = 1 - (t - 0.75) * 4;
        d.mat.update();
      }
    }
  }
}
